import { HexColor } from '@nx-smartmonkey/shared/domain';
import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { RoutalDefaultColors } from '@nx-smartmonkey/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { vehicleService } from '../../application/VehicleService';
import { Vehicle } from '../../domain/vehicle/Vehicle';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { getOrganizationResourcesMutationKey } from '../organization';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveFlatVehicles } from './useRetrieveFlatVehicles';

interface UseCreateVehiclesProps {
  vehicles: Array<Vehicle>;
}

type UseCreateVehiclesResponse = ServiceResponse<({ vehicles }: UseCreateVehiclesProps) => void, void>;

// Hooks act as 'Adapter' layer.
export const useCreateVehicles = (): UseCreateVehiclesResponse => {
  const queryClient = useQueryClient();
  const { data: activeProject } = useRetrieveActiveProject();
  const { run: refetchFlatVehicles, data: projectFlatVehicles } = useRetrieveFlatVehicles({ project: activeProject });
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    async ({ vehicles }: UseCreateVehiclesProps): Promise<Array<Vehicle>> => {
      // Get the existing vehicles colors.
      const flatVehiclesColors = (projectFlatVehicles ?? [])
        .map((flatVehicle) => (flatVehicle.color?.value ?? ``).toLowerCase())
        .filter((color) => color !== undefined);

      // Filter the available colors that are already not used for old vehicles.
      let sampleColors = RoutalDefaultColors.filter(
        (defaultColor) => !flatVehiclesColors.includes(defaultColor.toLowerCase())
      );

      // Setting the vehicle color
      vehicles.forEach((vehicle, index) => {
        // The new vehicle color should be one of the sample colors. If sample colors is empty, picks one of the RoutalDefaultColors
        const colorToUse =
          vehicle.color ??
          HexColor.create(
            sampleColors.length > 0
              ? sampleColors[index % sampleColors.length]
              : RoutalDefaultColors[index % RoutalDefaultColors.length]
          );

        vehicle.color = colorToUse;

        // Updates the sampleColors list removing the already used color.
        sampleColors = sampleColors.filter((color) => color !== colorToUse.value);
      });

      return vehicleService.createVehicles({ vehicles, project: activeProject! });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([`planner_vehicles`]);
        queryClient.invalidateQueries([`planner_vehicles_enabled`]);
        queryClient.invalidateQueries([`planner_flat_vehicles`]);
        queryClient.invalidateQueries(getOrganizationResourcesMutationKey());
        refetchFlatVehicles();
      },
      onError: (err: any) => {
        console.info(`Create Vehicles Error: `, err?.response);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    run: ({ vehicles }: UseCreateVehiclesProps) => mutate({ vehicles }),
  };
};
